import useBook from "api/BookResource/useBook";
import useSimilarBooks from "api/BookResource/useSimilarBooks";
import StarRating from "components/v2/BookItem/StarRating";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import React from "react";
import { Link, useParams } from "react-router-dom";
import useCurrencyStore from "store/currencyStore";
import useAddToPicks from "api/BookResource/useAddToPicks";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import useFreeBooksQuota from "api/BookResource/useFreeBooksQuota";
import usePrice from "hooks/usePrice";
import useAllowedBookActions from "hooks/useAllowedBookActions";
import useRemoveFromLibrary from "api/BookResource/useRemoveFromLibrary";
import useAddToCart from "api/BookResource/useAddToCart";
import useCheckBookAvailability from "api/BookResource/useCheckBookAvailability";
import AddToCartSuccessful from "components/v2/AddToCartSuccessful";
import GeoRestrictionWarning from "components/GeoRestrictionWarning";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import ActionsCard from "./ActionsCard";
import BookFormatLabel from "./BookFormatLabel";
import BookPageLoader from "./BookPageLoader";
import useBookEngagement from "hooks/engagement/useBookEngagement";
import useAddToWishlist from "api/wishlistResource/useAddToWishlist";
import useRemoveFromWishlist from "api/wishlistResource/useRemoveFromWishlist";
import ActionErrorMsg from "./ActionErrorMsg";
import useCreateRating from "api/RatingResource/useCreateRating";
import { BookListItem } from "components";

const Book = () => {
	const { bookId } = useParams();
	const { currency } = useCurrencyStore();
	const { data: book, isLoading: isLoadingBook } = useBook(bookId);
	const { data: isSubscribed, isLoading: isLoadingSubscribed } = useCheckIfUserIsSubscribed();
	const freeBooksQuota = useFreeBooksQuota();
	const { data: similarBooks } = useSimilarBooks(bookId);
	const { data: availability } = useCheckBookAvailability(bookId, {
		enabled: !!book?.geo_restricted,
	});

	const { sendAddToCartSuccess } = useBookEngagement(bookId, {
		heartbeat: { heartbeatIntervalMs: 15000, minElapsedMs: 5000, minScrollDelta: 5 },
	});

	const [cartQuantity, setCartQuantity] = React.useState(1);
	const [format, setFormat] = React.useState("digital");
	const [userRating, setUserRating] = React.useState(0);
	const [coverTilt, setCoverTilt] = React.useState({ rotateX: 0, rotateY: 0 });
	const [canHover, setCanHover] = React.useState(false);

	React.useEffect(() => {
		const mq = window.matchMedia("(hover: hover)");
		setCanHover(mq.matches);
		const handler = (e) => setCanHover(e.matches);
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, []);

	const { hasRoles, loggedIn } = useAuthStore();
	const { mutateAsync: addToWishlist, isLoading: isAddingToWishlist } = useAddToWishlist();
	const { mutateAsync: removeFromWishlist, isLoading: isRemovingFromWishlist } =
		useRemoveFromWishlist();

	const handleWishlistToggle = async (e) => {
		e?.stopPropagation?.();
		if (!loggedIn) return;
		try {
			if (book?.in_wishlist) {
				await removeFromWishlist(bookId);
			} else {
				await addToWishlist(bookId);
			}
		} catch {
			// Error handled by mutation
		}
	};

	const handleChangeCartQuantity = (value) => {
		const parsedValue = Number(value);
		if (!parsedValue || parsedValue <= 0) return setCartQuantity(1);
		return setCartQuantity(parsedValue);
	};

	const handleCoverMouseMove = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const x = (event.clientX - rect.left) / rect.width;
		const y = (event.clientY - rect.top) / rect.height;
		const rotateY = (x - 0.5) * 10;
		const rotateX = (0.5 - y) * 10;
		setCoverTilt({ rotateX, rotateY });
	};

	const resetCoverTilt = () => {
		setCoverTilt({ rotateX: 0, rotateY: 0 });
	};

	const price = usePrice({
		isFree: Boolean(book?.isFree),
		usdPrice: book?.price,
		zwlPrice: book?.zwl_price,
		sellable: Boolean(book?.sellable),
	});

	const physicalPrice = usePrice({
		isFree: Boolean(book?.isFree),
		usdPrice: book?.physical_usd_price,
		zwlPrice: book?.physical_zwl_price,
		sellable: Boolean(book?.sellable),
	});

	const { addToCart, addToPicks, removeFromPicks } = useAllowedBookActions({
		isFree: Boolean(book?.isFree),
		in_library: Boolean(book?.in_library),
		is_purchased: Boolean(book?.is_purchased),
		sellable: Boolean(book?.sellable),
		subscribable: Boolean(book?.subscription),
		hasSubscription: Boolean(isSubscribed?.data),
		freeBooksQuota,
	});

	const { mutateAsync, isLoading: isAddingToPicks } = useAddToPicks();
	const handleAddToPicks = () => {
		mutateAsync({ booksToSubscribe: book?.id });
	};

	const { mutateAsync: mutateRemoveFromLibrary, isLoading: isRemovingFromLibrary } =
		useRemoveFromLibrary(book?.id);
	const removeFromLibrary = async () => {
		await mutateRemoveFromLibrary();
	};

	const { mutateAsync: mutateAddToCart, isLoading: isAddingToCart } = useAddToCart();
	const { mutateAsync: mutateCreateRating, isLoading: isSubmittingRating } =
		useCreateRating(bookId);

	const handleAddToCart = async () => {
		try {
			await mutateAddToCart({
				book_id: bookId,
				quantity: cartQuantity,
				format,
			});
			sendAddToCartSuccess();
		} catch (error) {
			// noop - do not track on failure
		}
	};

	const handleRateBook = async (score) => {
		if (!loggedIn || isSubmittingRating) return;

		const previousValue = Number(book?.user_rating ?? 0);
		const selectedValue = Number(score);
		setUserRating(selectedValue);

		try {
			await mutateCreateRating({
				book_id: bookId,
				score: selectedValue,
			});
		} catch (error) {
			setUserRating(previousValue);
		}
	};

	React.useEffect(() => {
		setUserRating(Number(book?.user_rating ?? 0));
	}, [book?.user_rating, book?.id]);

	const isLoading = isLoadingBook || isLoadingSubscribed;

	if (isLoading) {
		return <BookPageLoader />;
	}

	const showGeoWarning = Boolean(book?.geo_restricted) && Boolean(availability);
	const geoWarning = showGeoWarning ? (
		<GeoRestrictionWarning availability={availability.data} />
	) : null;

	const actionsCard = (
		<div className="bg-white/95 rounded-2xl border border-gray-100 p-4 md:p-6 shadow-xl shadow-gray-200/50 backdrop-blur-sm">
			<ActionsCard
				price={price}
				physicalPrice={physicalPrice}
				currency={currency}
				book={book}
				addToPicks={addToPicks}
				handleAddToPicks={handleAddToPicks}
				removeFromPicks={removeFromPicks}
				isAddingToPicks={isAddingToPicks}
				isRemovingFromLibrary={isRemovingFromLibrary}
				removeFromLibrary={removeFromLibrary}
				addToCart={addToCart}
				isAddingToCart={isAddingToCart}
				handleAddToCart={handleAddToCart}
				cartQuantity={cartQuantity}
				handleChangeCartQuantity={handleChangeCartQuantity}
				hasRoles={hasRoles}
				Role={Role}
				format={format}
				setFormat={setFormat}
				isInWishlist={loggedIn ? book?.in_wishlist : undefined}
				handleWishlistToggle={loggedIn ? handleWishlistToggle : undefined}
				isAddingToWishlist={isAddingToWishlist}
				isRemovingFromWishlist={isRemovingFromWishlist}
			/>
		</div>
	);

	const ratingPromptAction = {
		label: "Login",
		link: `/login?intended=/book/${bookId}`,
	};

	const averageRating = Number(book?.rating ?? 0);

	return (
		<Container>
			<PaddingX>
				<main className="max-w-7xl mx-auto pt-8 md:pt-10 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] overflow-x-hidden">
					<div className="mb-5 md:mb-7">
						<nav
							aria-label="Book page breadcrumb"
							className="flex flex-wrap items-center gap-2 text-sm text-gray-500"
						>
							<Link className="hover:text-akgreener transition-colors" to="/">
								Home
							</Link>
							<span aria-hidden>/</span>
							<Link className="hover:text-akgreener transition-colors" to="/books">
								Books
							</Link>
							<span aria-hidden>/</span>
							<span className="text-gray-700 font-medium truncate max-w-[180px] xs:max-w-[220px] sm:max-w-xs md:max-w-none">
								{book?.title}
							</span>
						</nav>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
						<div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 md:gap-8 min-w-0">
							<div className="md:col-span-4 flex justify-center min-w-0">
								<div className="w-full max-w-[200px] xxs:max-w-[240px] xs:max-w-[260px] sm:max-w-[280px] md:max-w-[300px] md:sticky md:top-8">
									<div className="relative isolate overflow-hidden">
										<div
											className="pointer-events-none absolute -inset-4 xs:-inset-6 rounded-3xl bg-gradient-to-br from-akgreener/20 via-akgreenest/10 to-transparent blur-2xl hidden xxs:block"
											style={{ animation: "pulse 3.6s ease-in-out infinite" }}
										/>
										<svg
											className="pointer-events-none absolute -top-6 -right-6 xs:-top-8 xs:-right-8 h-14 w-14 xs:h-20 xs:w-20 text-akgreener/45 hidden xxs:block"
											viewBox="0 0 120 120"
											fill="none"
											aria-hidden
											style={{ animation: "spin 18s linear infinite" }}
										>
											<circle
												cx="60"
												cy="60"
												r="42"
												stroke="currentColor"
												strokeWidth="2.5"
												strokeDasharray="7 8"
											/>
											<circle cx="103" cy="60" r="5" fill="currentColor" />
										</svg>

										<div
											className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-xl transition-transform duration-300"
											style={{
												aspectRatio: "3/4",
												transform: canHover
													? "perspective(1200px) rotateX(" +
														coverTilt.rotateX +
														"deg) rotateY(" +
														coverTilt.rotateY +
														"deg)"
													: undefined,
											}}
											onMouseMove={canHover ? handleCoverMouseMove : undefined}
											onMouseLeave={canHover ? resetCoverTilt : undefined}
										>
											{book?.book_cover?.url ? (
												<img
													src={book?.book_cover?.url}
													alt={book?.title}
													className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
												/>
											) : (
												<div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200" />
											)}
											{/* <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-xl border border-white border-opacity-30 bg-black bg-opacity-20 p-2 text-xs text-white backdrop-blur-sm">
												Preview this title
											</div> */}
										</div>
									</div>
								</div>
							</div>

							<div className="md:col-span-8 space-y-5 md:space-y-6 min-w-0">
								{book?.has_preview && book?.preview_url && (
									<a
										href={book.preview_url}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-akgreener/25 bg-akgreen/10 text-akgreen hover:bg-akgreen/20 hover:-translate-y-0.5 active:translate-y-0 transition-all font-medium text-sm"
									>
										<span className="h-2 w-2 rounded-full bg-akgreener animate-pulse" />
										Read Sample
									</a>
								)}
								<section className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 md:p-7 shadow-sm">
									<div className="pointer-events-none absolute -top-14 -right-14 h-40 w-40 rounded-full bg-akgreener/10 blur-2xl" />
									<div className="pointer-events-none absolute -bottom-16 -left-16 h-36 w-36 rounded-full bg-akgreenest/10 blur-2xl" />
									<div className="space-y-4">
										<div className="flex flex-wrap items-center gap-2">
											<BookFormatLabel format={book?.format} />
											<div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 transition-shadow duration-200 hover:shadow-sm">
												<StarRating
													number={book?.rating}
													value={loggedIn ? userRating : averageRating}
													readOnly={!loggedIn || isSubmittingRating}
													onChange={handleRateBook}
												/>
												<span className="text-xs font-medium text-gray-600">
													{loggedIn
														? userRating > 0
															? `You rated ${userRating}/5`
															: "Tap to rate"
														: `${averageRating.toFixed(1)} average`}
												</span>
											</div>
										</div>

										{!loggedIn && (
											<ActionErrorMsg
												heading="Rate this book"
												message="Please log in to submit your own rating."
												action={ratingPromptAction}
												className="inline-flex items-center rounded-full border border-akgreener/30 bg-akgreener/5 px-3 py-1 text-xs font-medium text-akgreenest hover:bg-akgreener/10 transition-colors"
											>
												Login to rate this book
											</ActionErrorMsg>
										)}

										<header className="space-y-3">
											<h1 className="text-xl xs:text-2xl md:text-2xl lg:text-3xl font-cera-black leading-tight tracking-tight text-gray-700">
												{book?.title}
											</h1>

											<div className="flex flex-wrap gap-3 md:gap-4 text-sm md:text-base text-gray-600">
												{book?.author && (
													<div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5">
														<span className="text-gray-500">Written by</span>
														<span className="font-semibold text-gray-900">{book?.author}</span>
													</div>
												)}

												{book?.publisher && (
													<div className="inline-flex items-center gap-2 rounded-full border border-akgreener/20 bg-akgreener/5 px-3 py-1.5">
														<span className="text-gray-500">Published by</span>
														<span className="font-semibold text-akgreenest">{book?.publisher}</span>
													</div>
												)}
											</div>
										</header>
									</div>
								</section>

								<div className="space-y-4 lg:hidden">
									{geoWarning}
									{actionsCard}
								</div>

								{book?.description && (
									<section className="rounded-2xl border border-gray-100 bg-white p-5 md:p-7 shadow-sm">
										<h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
											About this book
										</h2>
										<div className="prose prose-sm md:prose-base max-w-none text-gray-700">
											<div
												className="leading-relaxed"
												dangerouslySetInnerHTML={{
													__html: book?.description,
												}}
											/>
										</div>
									</section>
								)}

								{Array.isArray(similarBooks) && similarBooks.length > 0 && (
									<section className="rounded-2xl border border-gray-100 bg-white p-5 md:p-7 shadow-sm">
										<h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
											Readers Also Enjoyed
										</h2>
										<div className="grid grid-cols-1 xs:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
											{similarBooks.slice(0, 4).map((similarBook) => (
												<div key={similarBook.id} className="min-w-0">
													<BookListItem
													coverUrl={similarBook?.book_cover?.url}
													title={similarBook?.title}
													description={similarBook?.description}
													price={similarBook?.price}
													zwl_price={similarBook?.zwl_price}
													credits={similarBook.credits}
													publisher={similarBook?.publisher || `${similarBook?.user?.first_name} ${similarBook?.user?.last_name}`}
													author={similarBook?.author}
													reviews={similarBook.ratings}
													bookId={similarBook.id}
													sellable={similarBook.sellable}
													rating={similarBook.rating}
													isPurchased={similarBook.is_purchased}
													inLibrary={similarBook.in_library}
													subscription={similarBook.subscription}
													category={similarBook?.category?.name}
													isFree={similarBook?.isFree}
													format={similarBook?.format}
													genres={similarBook?.genres?.map((g) => [g?.id, g?.name])}
													showWishlist={loggedIn}
													isInWishlist={similarBook?.in_wishlist}
													hasPreview={similarBook?.has_preview}
												/>
												</div>
											))}
										</div>
									</section>
								)}
							</div>
						</div>

						<div className="hidden lg:block lg:col-span-4">
							<div className="lg:sticky lg:top-8 space-y-4">
								{geoWarning}
								{actionsCard}
							</div>
						</div>
					</div>
				</main>
			</PaddingX>

			<AddToCartSuccessful />
		</Container>
	);
};

export default Book;
